import type React from "react"
import { useTranslation } from "react-i18next"
import { Outlet, useMatches } from "react-router"

import { AuthenticatedPageLayout } from "@/components/authenticated-page-layout"
import { Page404 } from "@/components/nav/page404"
import { useAuthentication } from "@/contexts"
import {
  PAGE_LAYOUT_FILL,
  resolvePageLayoutMode,
} from "@/utils/page-layout-mode"

import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"

import { AdminSecondaryNav } from "./admin-secondary-nav"

export const AdminRoute: React.FC = () => {
  const { t } = useTranslation("common")
  const matches = useMatches()
  const layoutMode = resolvePageLayoutMode(matches, "adminLayout")
  const isFill = layoutMode === PAGE_LAYOUT_FILL
  const { isAdminUser, isOrgAdminUser, isOrgCredentialsAdminUser } =
    useAuthentication()

  if (!isAdminUser && !isOrgAdminUser && !isOrgCredentialsAdminUser) {
    return <Page404 />
  }

  return (
    <AuthenticatedPageLayout
      fullHeight={isFill}
      title={t("moduleTitle.administration")}
    >
      <Stack
        sx={{
          flexDirection: { xs: "column", md: "row" },
          ...(isFill
            ? { height: "100%", minHeight: 0 }
            : { minHeight: "100%" }),
        }}
      >
        <Box
          sx={{
            alignSelf: "flex-start",
            flexShrink: 0,
            position: { md: "sticky" },
            // Sit below sticky PageHeader (76) + divider (1) + Page padding (24).
            top: 101,
            zIndex: { md: 1 },
          }}
        >
          <AdminSecondaryNav />
        </Box>
        {/*
          Do not put overflow clipping on this column — PageCard shadows must
          remain visible. Fill mode bounds height via Page fullHeight; scroll
          mode grows with content and uses the page scrollport.
        */}
        <Box
          data-admin-layout={layoutMode}
          sx={{
            flex: 1,
            minWidth: 0,
            ...(isFill ? { minHeight: 0 } : {}),
          }}
        >
          <Outlet />
        </Box>
      </Stack>
    </AuthenticatedPageLayout>
  )
}
